import { graphql, Link, useStaticQuery } from 'gatsby';
import React, { ReactElement, useEffect, useState } from 'react';
import {
  Headline,
  Input,
  Link as StarkLink,
  Text,
} from '../../../../../components';
import IconDisclose from '../../../../icons/icon-disclose';
import IconSearch from '../../../../icons/icon-search';
import './style.css';

// The left side navigation that is present on all library pages.
// Also handles input changes to the search form.
export default function LibraryNav({
  onSearchInputChange,
  onSearch,
  onHideSearchResults,
}: any): ReactElement {
  const data = useStaticQuery(graphql`
    {
      allStrapiLibraryCategory {
        nodes {
          name
          slug
        }
      }
    }
  `);

  // Categories are handled via the /src/content/library-categories file.
  // Add a category there to have it show up here.
  const [showCategories, setShowCategories] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Putting this at the top because of limitations of gatsby's history
  // to not include search params.
  let searchParam;
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    searchParam = urlParams.get('search');
  }

  useEffect(() => {
    const currentLoc = window.location.pathname;

    // They are on a category page so we should show them the list of
    // categories so they can easily switch between them
    if (
      currentLoc !== '/library/' &&
      !currentLoc.startsWith('/library/collections')
    ) {
      setShowCategories(true);
    }
  }, []);

  // This is necessary to ensure the form input shows the correct
  // search param
  useEffect(() => {
    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [searchParam]);

  const handleCategoriesClick = () => {
    setShowCategories(!showCategories);
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    onSearchInputChange(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  return (
    <nav className="library-nav pt-16" aria-label="Library category navigation">
      <div className="library-nav__mobile">
        <Link
          to="/library/"
          className="text text--semibold"
          activeClassName="library-nav--active"
          onClick={() => onHideSearchResults()}>
          Library
        </Link>

        <Link
          to="/library/collections/"
          className="text text--semibold"
          activeClassName="library-nav--active"
          partiallyActive
          onClick={() => onHideSearchResults()}>
          Collections
        </Link>

        <button
          className={`library-nav__categories-text text text--semibold${
            showCategories ? ' library-nav__categories-text--active' : ''
          }`}
          tabIndex={0}
          onClick={handleCategoriesClick}>
          <span className="flex items-center">Categories</span>

          <IconDisclose className="ml-5" />
        </button>

        <div className="library-nav__search relative">
          <IconSearch className="pointer-events-none absolute left-3 top-3" />

          <form onSubmit={handleSearchSubmit}>
            <Input
              value={searchQuery}
              minLength={3}
              required
              onChange={handleInputChange}
            />
          </form>
        </div>
      </div>

      {showCategories && (
        <div className="library-nav__categories--mobile">
          <Link
            key="all"
            to={`/library/all/`}
            className="text text--semibold"
            activeClassName="library-nav--active"
            onClick={() => onHideSearchResults()}>
            All
          </Link>
          {data.allStrapiLibraryCategory.nodes.map(({ name, slug }) => (
            <Link
              key={name}
              to={`/library/${slug}/`}
              className="text text--semibold"
              activeClassName="library-nav--active"
              onClick={() => onHideSearchResults()}>
              {name}
            </Link>
          ))}
        </div>
      )}

      <div id="libraryNav" className="library-nav__container">
        <div className="library-nav__search relative mb-4">
          <IconSearch className="pointer-events-none absolute left-3 top-[42px]" />

          <form onSubmit={handleSearchSubmit}>
            <Input
              id="SearchLibraryInput"
              label="Search Library"
              value={searchQuery}
              minLength={3}
              required
              onChange={handleInputChange}
            />
          </form>
        </div>

        <Link
          to="/library/"
          className="text text--semibold"
          activeClassName="library-nav--active"
          onClick={() => onHideSearchResults()}>
          Home
        </Link>

        <Link
          to="/library/collections/"
          className="text text--semibold"
          activeClassName="library-nav--active"
          partiallyActive
          onClick={() => onHideSearchResults()}>
          Collections
        </Link>

        <button
          className={`library-nav__categories-text text mb-2 text--semibold${
            showCategories ? ' library-nav__categories-text--active' : ''
          }`}
          tabIndex={0}
          onClick={handleCategoriesClick}>
          <span className="flex items-center">Categories</span>
          <IconDisclose className="ml-auto mr-5" />
        </button>

        {showCategories && (
          <div className="library-nav__categories">
            <Link
              key="all"
              to={`/library/all/`}
              className="text text--semibold"
              activeClassName="library-nav--active"
              onClick={() => onHideSearchResults()}>
              All
            </Link>
            {data.allStrapiLibraryCategory.nodes.map(({ name, slug }) => (
              <Link
                key={name}
                to={`/library/${slug}/`}
                className="text text--semibold"
                activeClassName="library-nav--active"
                onClick={() => onHideSearchResults()}>
                {name}
              </Link>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-lg bg-base-purple-100 p-6">
          <Headline level="4" className="text-type-blue">
            Contribute to Stark
          </Headline>

          <Text color="blue" className="mb-6 mt-2">
            Got any accessibility resources or news?
          </Text>

          <StarkLink
            style={{ minHeight: 44 }}
            linkType="button"
            target="_blank"
            href="https://forms.gle/V15UinvhcEePiJVm7"
            className="w-full pr-4 pl-4">
            Submit a Resource
          </StarkLink>
        </div>
      </div>
    </nav>
  );
}
